import React from "react";
import { useModalForm } from "../../hooks/useModalForm";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import TextInput from "../fields/TextInput";
import { MdSave } from "react-icons/md";
import {
  WorkoutLocation,
  FitnessGoal,
  FocusArea,
  BodyComposition,
  ProgramDifficultyLevelLabels,
  WorkoutLocationLabels,
  FitnessGoalLabels,
  FocusAreaLabels,
  BodyCompositionLabels,
  STEP_TARGET_CONSTRAINTS,
  TRAINING_FREQUENCY_CONSTRAINTS,
  UpdateFitnessDataInput,
} from "../../types/fitness";
import { FitnessData } from "../../types/user";
import ModalShell from "../modals/ModalShell";
import { selectClass, FIELD_PLACEHOLDER } from "../fields/fieldStyles";
import Button from "../common/Button";

interface FitnessDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UpdateFitnessDataInput) => Promise<void>;
  fitnessData: FitnessData | null | undefined;
  loading?: boolean;
  error?: string;
}

// Pure so useModalForm can call it; it used to setErrors itself.
function validateFitnessData(
  formData: UpdateFitnessDataInput
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  if (!formData.fitness_goal || formData.fitness_goal.length === 0) {
    newErrors.fitness_goal = "At least one fitness goal is required";
  }

  if (!formData.focus_area || formData.focus_area.length === 0) {
    newErrors.focus_area = "At least one focus area is required";
  }

  if (!formData.fitness_level) {
    newErrors.fitness_level = "Fitness level is required";
  }

  if (!formData.workout_location) {
    newErrors.workout_location = "Workout location is required";
  }

  if (!formData.body_composition) {
    newErrors.body_composition = "Body composition goal is required";
  }

  if (
    formData.training_frequency !== undefined &&
    formData.training_frequency !== ""
  ) {
    const freq = parseInt(formData.training_frequency);
    if (
      isNaN(freq) ||
      freq < TRAINING_FREQUENCY_CONSTRAINTS.MIN ||
      freq > TRAINING_FREQUENCY_CONSTRAINTS.MAX
    ) {
      newErrors.training_frequency = `Training frequency must be between ${TRAINING_FREQUENCY_CONSTRAINTS.MIN} and ${TRAINING_FREQUENCY_CONSTRAINTS.MAX} days/week`;
    }
  } else {
    newErrors.training_frequency = "Training frequency is required";
  }

  if (formData.step_target !== undefined) {
    if (
      formData.step_target < STEP_TARGET_CONSTRAINTS.MIN ||
      formData.step_target > STEP_TARGET_CONSTRAINTS.MAX
    ) {
      newErrors.step_target = `Step target must be between ${STEP_TARGET_CONSTRAINTS.MIN.toLocaleString()} and ${STEP_TARGET_CONSTRAINTS.MAX.toLocaleString()} steps`;
    }
  } else {
    newErrors.step_target = "Daily step target is required";
  }

  return newErrors;
}

const FitnessDataModal: React.FC<FitnessDataModalProps> = ({
  isOpen,
  onClose,
  onSave,
  fitnessData,
  loading = false,
  error,
}) => {
  const parseFitnessGoals = (data: string | null | any): FitnessGoal[] => {
    if (!data) return [];

    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  };

  const parseFocusAreas = (data: string | null | any): FocusArea[] => {
    if (!data) return [];

    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  };

  const {
    values: formData,
    setValues: setFormData,
    errors,
    handleSubmit: handleSave,
  } = useModalForm<UpdateFitnessDataInput>({
    isOpen,
    initialValues: {
      fitness_level: fitnessData?.fitness_level || "",
      workout_location:
        (fitnessData?.workout_location as WorkoutLocation) || undefined,
      fitness_goal: parseFitnessGoals(fitnessData?.fitness_goal),
      training_frequency: fitnessData?.training_frequency?.toString() || "",
      focus_area: parseFocusAreas(fitnessData?.focus_area),
      body_composition:
        (fitnessData?.body_composition as BodyComposition) || undefined,
      step_target: fitnessData?.step_target || undefined,
    },
    validate: validateFitnessData,
    onSubmit: onSave,
  });

  if (!isOpen) return null;

  const handleMultiSelectChange = (
    field: "fitness_goal" | "focus_area",
    value: string
  ) => {
    if (field === "fitness_goal") {
      const currentValues = formData.fitness_goal || [];
      const newValues = currentValues.includes(value as FitnessGoal)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value as FitnessGoal];
      setFormData((prev) => ({ ...prev, fitness_goal: newValues }));
    } else {
      const currentValues = formData.focus_area || [];
      const newValues = currentValues.includes(value as FocusArea)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value as FocusArea];
      setFormData((prev) => ({ ...prev, focus_area: newValues }));
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Fitness Data"
      size="4xl"
      closeDisabled={loading}
      message={error ? { text: error, type: "error" } : null}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={loading}
            loadingLabel="Saving..."
          >
            <MdSave className="h-4 w-4" />
            Save Changes
          </Button>
        </>
      }
    >
      {/* Body */}
      <div className="space-y-6 p-6">
        {/* Training Profile Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-biafit-primary">
            Training Profile
          </h3>

          {/* Fitness Goal (multi-select) */}
          <div>
            <Label>
              Fitness Goal <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(FitnessGoalLabels).map(([key, label]) => (
                <label
                  key={key}
                  className={`flex cursor-pointer items-center rounded-md border px-3 py-2 transition-colors ${
                    formData.fitness_goal?.includes(key as FitnessGoal)
                      ? "border-biafit-primary bg-biafit-primary text-white"
                      : "border-gray-300 bg-white hover:border-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      formData.fitness_goal?.includes(key as FitnessGoal) ||
                      false
                    }
                    onChange={() =>
                      handleMultiSelectChange("fitness_goal", key)
                    }
                    className="hidden"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <FieldError>{errors.fitness_goal}</FieldError>
          </div>

          {/* Focus Area (multi-select) */}
          <div>
            <Label>
              Focus Area <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Object.entries(FocusAreaLabels).map(([key, label]) => (
                <label
                  key={key}
                  className={`flex cursor-pointer items-center rounded-md border px-3 py-2 transition-colors ${
                    formData.focus_area?.includes(key as FocusArea)
                      ? "border-biafit-primary bg-biafit-primary text-white"
                      : "border-gray-300 bg-white hover:border-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      formData.focus_area?.includes(key as FocusArea) || false
                    }
                    onChange={() => handleMultiSelectChange("focus_area", key)}
                    className="hidden"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <FieldError>{errors.focus_area}</FieldError>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Fitness Level */}
            <div>
              <Label>
                Fitness Level <span className="text-red-500">*</span>
              </Label>
              <select
                value={formData.fitness_level || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    fitness_level: e.target.value,
                  }))
                }
                className={selectClass(
                  !!errors.fitness_level,
                  formData.fitness_level ? "" : FIELD_PLACEHOLDER
                )}
              >
                <option value="" disabled>
                  Select fitness level
                </option>
                {Object.entries(ProgramDifficultyLevelLabels).map(
                  ([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  )
                )}
              </select>
              <FieldError>{errors.fitness_level}</FieldError>
            </div>

            {/* Workout Location */}
            <div>
              <Label>
                Workout Location <span className="text-red-500">*</span>
              </Label>
              <select
                value={formData.workout_location || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    workout_location: e.target.value as WorkoutLocation,
                  }))
                }
                className={selectClass(
                  !!errors.workout_location,
                  formData.workout_location ? "" : FIELD_PLACEHOLDER
                )}
              >
                <option value="" disabled>
                  Select workout location
                </option>
                {Object.entries(WorkoutLocationLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError>{errors.workout_location}</FieldError>
            </div>

            {/* Training Frequency */}
            <div>
              <Label>
                Training Frequency <span className="text-red-500">*</span>
              </Label>
              <TextInput
                type="number"
                value={formData.training_frequency || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    training_frequency: e.target.value,
                  }))
                }
                min={TRAINING_FREQUENCY_CONSTRAINTS.MIN}
                max={TRAINING_FREQUENCY_CONSTRAINTS.MAX}
                invalid={!!errors.training_frequency}
                placeholder="Days per week (1-7)"
              />
              <FieldError>{errors.training_frequency}</FieldError>
            </div>

            {/* Body Composition */}
            <div>
              <Label>
                Body Composition Goal <span className="text-red-500">*</span>
              </Label>
              <select
                value={formData.body_composition || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    body_composition: e.target.value as BodyComposition,
                  }))
                }
                className={selectClass(
                  !!errors.body_composition,
                  formData.body_composition ? "" : FIELD_PLACEHOLDER
                )}
              >
                <option value="" disabled>
                  Select body composition goal
                </option>
                {Object.entries(BodyCompositionLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError>{errors.body_composition}</FieldError>
            </div>
          </div>
        </div>

        {/* Activity Targets Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-biafit-primary">
            Activity Targets
          </h3>
          <div>
            <Label>
              Daily Step Target <span className="text-red-500">*</span>
            </Label>
            <TextInput
              type="number"
              value={formData.step_target || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  step_target: parseInt(e.target.value) || undefined,
                }))
              }
              min={STEP_TARGET_CONSTRAINTS.MIN}
              max={STEP_TARGET_CONSTRAINTS.MAX}
              invalid={!!errors.step_target}
              placeholder="e.g., 10000"
            />
            <FieldError>{errors.step_target}</FieldError>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default FitnessDataModal;
